import { useEffect, useRef } from 'react';

import { formatRelativeOrAbsolute, toMachineDate } from '@shared/format';
import { cn } from '@shared/ui/cn';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { StateBadge } from '@shared/ui/StateBadge';

import { copy } from './copy';
import { isUnread, type NotificationRow } from './types';

export const TABLE_COLUMNS = 4;

/**
 * The inbox table — four columns, and every message row followed by its own
 * detail row.
 *
 * **The message cell is a button, not a link.** Row activation navigating to a
 * notification's subject is out of scope, so nothing here is a destination; what
 * the control does is disclose the body **in place**, below the row, without
 * leaving the list the operator is working. A `<dialog>` would satisfy «in full»
 * and break «in place».
 *
 * **The detail row is a table row.** It spans all four columns and holds a panel
 * so it reads as a panel inside the table rather than as a fifth kind of row.
 * It is in the document at all times and hidden with the `hidden` **attribute**,
 * so it leaves the accessibility tree and the tab order together — and so paper,
 * which has no disclosure, can show every body at once.
 *
 * **Opening a message marks it read.** Leaving a message unread after its whole
 * body has been read on screen makes the handle's badge count work that is
 * already done. It is the one place on this surface where a read writes state,
 * and it writes the only state a read can honestly write.
 */
export function InboxTable({
  rows,
  pending,
  selected,
  expanded,
  onToggleRow,
  onToggleAll,
  onOpenRow,
}: {
  rows: readonly NotificationRow[];
  pending: boolean;
  selected: ReadonlySet<string>;
  expanded: ReadonlySet<string>;
  onToggleRow: (id: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  onOpenRow: (row: NotificationRow) => void;
}) {
  const selectAllRef = useRef<HTMLInputElement>(null);
  const selectedCount = rows.filter((row) => selected.has(row.id)).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;

  useEffect(() => {
    // The third state exists only on the element and not as an attribute — an
    // invented attribute would render nothing and announce nothing.
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && selectedCount < rows.length;
    }
  }, [selectedCount, rows.length]);

  return (
    <DTableScroll>
      <DTable caption={copy.section.caption}>
        <DTableHead>
          <tr>
            <th scope="col" className="w-px whitespace-nowrap print:hidden">
              <label className="inline-grid cursor-pointer place-items-center p-2">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelected}
                  disabled={rows.length === 0}
                  onChange={(event) => onToggleAll(event.currentTarget.checked)}
                  className="accent-accent"
                />
                <span className="sr-only">{copy.columns.selectAll}</span>
              </label>
            </th>
            <th scope="col">{copy.columns.message}</th>
            <th scope="col">{copy.columns.date}</th>
            <th scope="col">{copy.columns.state}</th>
          </tr>
        </DTableHead>

        <DTableBody>
          {pending ? (
            <RowSkeleton columns={TABLE_COLUMNS} rows={4} />
          ) : (
            rows.map((row) => (
              <InboxRow
                key={row.id}
                row={row}
                selected={selected.has(row.id)}
                expanded={expanded.has(row.id)}
                onToggleRow={onToggleRow}
                onOpenRow={onOpenRow}
              />
            ))
          )}
        </DTableBody>
      </DTable>
    </DTableScroll>
  );
}

function InboxRow({
  row,
  selected,
  expanded,
  onToggleRow,
  onOpenRow,
}: {
  row: NotificationRow;
  selected: boolean;
  expanded: boolean;
  onToggleRow: (id: string, checked: boolean) => void;
  onOpenRow: (row: NotificationRow) => void;
}) {
  const unread = isUnread(row);
  const detailId = `note-${row.id}`;
  const stateLabel = unread ? copy.state.unread : copy.state.read;

  return (
    <>
      <tr
        // The unread row takes a light ground **beside** its badge, never
        // instead of one: colour is not the only signal, and the badge is text.
        className={cn(unread && '[&>*]:bg-bg-sunken')}
      >
        <td className="w-px whitespace-nowrap print:hidden">
          <label className="inline-grid cursor-pointer place-items-center p-2">
            <input
              type="checkbox"
              checked={selected}
              onChange={(event) => onToggleRow(row.id, event.currentTarget.checked)}
              className="accent-accent"
            />
            <span className="sr-only">{copy.columns.selectRow(row.headline)}</span>
          </label>
        </td>

        <DTableRowHeader>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={() => onOpenRow(row)}
            className="hit-target -mx-2 rounded-sm px-2 text-start font-semibold text-ink hover:bg-hover-bg"
          >
            {row.headline}
          </button>
        </DTableRowHeader>

        <td className="whitespace-nowrap">
          {/* The human string and the machine value on the same element. */}
          <time dateTime={toMachineDate(row.occurred_at)}>{formatRelativeOrAbsolute(row.occurred_at)}</time>
        </td>

        <td>
          <StateBadge kind={unread ? 'warning' : 'neutral'}>{stateLabel}</StateBadge>
        </td>
      </tr>

      <tr id={detailId} hidden={!expanded} className="print:!table-row">
        <td colSpan={TABLE_COLUMNS} className="!pt-0">
          <div className="rounded-md border border-border bg-surface-raised p-4">
            <p className="mb-0 max-w-measure text-sm text-ink">{row.body}</p>
            <p className="mb-0 mt-2 max-w-none text-xs text-ink-muted">
              <time dateTime={toMachineDate(row.occurred_at)}>
                {formatRelativeOrAbsolute(row.occurred_at)}
              </time>
              {' · '}
              <span>{stateLabel}</span>
            </p>
          </div>
        </td>
      </tr>
    </>
  );
}
